import { useEffect, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { TriangleAlert, X } from "lucide-react";
import { cn } from "@/lib/cn";
import {
  KbAdminSection,
  KbPrincipal,
  archiveKbSection,
  changeKbSectionOwner,
  createKbSection,
  fetchKbAudience,
  fetchKbTeams,
  restoreKbSection,
  searchKbEligibleUsers,
  setKbSectionEditors,
  setKbSectionVisibility,
  updateKbSectionSettings,
} from "@/api/knowledge";

/**
 * D-147 W1 S5 — drawer «Налаштування розділу» (visual package ⑥):
 * name/description · owner/backup (confirm «Право публікації перейде до
 * [ім'я]») · editors-chips · видимість «Усі співробітники | Обрані команди»
 * (chips + аудиторія-число + empty-DENY warning дослівно + missing-team chip)
 * · архів/відновлення розділу. Confirm звуження всі→команди дослівно.
 * Довідники (teams/eligible-users/audience) — KB_Admin-only ендпоінти;
 * не-admin owner редагує name/desc/editors у межах наявних кандидатів.
 */
export function KbSectionDrawer({
  section,
  isKbAdmin,
  onClose,
}: {
  /** null = створення нового розділу (KB_Admin). */
  section: KbAdminSection | null;
  isKbAdmin: boolean;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const creating = section === null;

  const [name, setName] = useState(section?.name ?? "");
  const [description, setDescription] = useState(section?.description ?? "");
  const [owner, setOwner] = useState<KbPrincipal | null>(section?.owner ?? null);
  const [backup, setBackup] = useState<KbPrincipal | null>(section?.backupOwner ?? null);
  const [editors, setEditors] = useState<KbPrincipal[]>(section?.editors ?? []);
  const [mode, setMode] = useState<"all" | "teams">(section?.visibilityMode ?? "all");
  const [teams, setTeams] = useState<{ id: string; name: string; missing?: boolean }[]>(
    section?.teams ?? [],
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirm, setConfirm] = useState<{ text: string; run: () => void } | null>(null);
  const [pickerFor, setPickerFor] = useState<"owner" | "backup" | "editor" | null>(null);
  const [pickerQuery, setPickerQuery] = useState("");

  const teamsQ = useQuery({
    queryKey: ["kbTeams"],
    queryFn: fetchKbTeams,
    enabled: isKbAdmin,
    staleTime: 60_000,
  });

  const aliveTeamIds = useMemo(() => teams.filter((t) => !t.missing).map((t) => t.id), [teams]);

  const audienceQ = useQuery({
    queryKey: ["kbAudience", aliveTeamIds.join(",")],
    queryFn: () => fetchKbAudience(aliveTeamIds),
    enabled: isKbAdmin && mode === "teams" && aliveTeamIds.length > 0,
    staleTime: 30_000,
  });

  const pickerSearch = useQuery({
    queryKey: ["kbEligibleUsers", pickerQuery],
    queryFn: () => searchKbEligibleUsers(pickerQuery),
    enabled: isKbAdmin && pickerFor !== null && pickerQuery.trim().length >= 2,
    staleTime: 30_000,
  });

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !busy) onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [busy, onClose]);

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ["kbAdminSections"] });
    void qc.invalidateQueries({ queryKey: ["kbHome"] });
    if (section) void qc.invalidateQueries({ queryKey: ["kbSection", section.id] });
  };

  const finish = () => {
    invalidate();
    onClose();
  };

  const fail = (e: unknown) => {
    setError(e instanceof Error ? e.message : "Не вдалося зберегти.");
    setBusy(false);
  };

  const doSave = async () => {
    setError(null);
    const trimmed = name.trim();
    if (trimmed === "" || !owner) {
      setError("Назва та власник обовʼязкові.");
      return;
    }

    setBusy(true);
    try {
      if (creating) {
        await createKbSection({
          name: trimmed,
          description,
          ownerId: owner.id,
          backupOwnerId: backup?.id,
          visibilityMode: mode,
          teamIds: aliveTeamIds,
          editorIds: editors.map((e) => e.id),
        });
        finish();
        return;
      }

      const s = section!;
      if (trimmed !== s.name || description !== s.description) {
        await updateKbSectionSettings(s.id, { name: trimmed, description });
      }
      if (owner.id !== s.owner?.id || (backup?.id ?? null) !== (s.backupOwner?.id ?? null)) {
        await changeKbSectionOwner(s.id, owner.id, backup?.id ?? null);
      }
      const oldEditors = (s.editors ?? []).map((e) => e.id).sort().join(",");
      const newEditors = editors.map((e) => e.id).sort().join(",");
      if (oldEditors !== newEditors) {
        await setKbSectionEditors(s.id, editors.map((e) => e.id));
      }
      const oldTeams = (s.teams ?? []).map((t) => t.id).sort().join(",");
      if (mode !== s.visibilityMode || aliveTeamIds.slice().sort().join(",") !== oldTeams) {
        await setKbSectionVisibility(s.id, mode, aliveTeamIds);
      }
      finish();
    } catch (e) {
      fail(e);
    }
  };

  const save = () => {
    // confirm-и дослівно (мокап ⑥)
    if (!creating && owner && owner.id !== section!.owner?.id) {
      setConfirm({
        text: `Право публікації перейде до ${owner.name}.`,
        run: () => {
          setConfirm(null);
          void confirmVisibilityThenSave();
        },
      });
      return;
    }
    void confirmVisibilityThenSave();
  };

  const confirmVisibilityThenSave = () => {
    if (!creating && mode === "teams" && section!.visibilityMode === "all") {
      setConfirm({
        text: `Розділ і всі ${section!.publishedCount} матеріалів стануть невидимими поза обраними командами (вкл. пошук та AI).`,
        run: () => {
          setConfirm(null);
          void doSave();
        },
      });
      return;
    }
    void doSave();
  };

  const pickPrincipal = (u: KbPrincipal) => {
    if (pickerFor === "owner") setOwner(u);
    else if (pickerFor === "backup") setBackup(u);
    else if (pickerFor === "editor" && !editors.some((e) => e.id === u.id))
      setEditors((prev) => [...prev, u]);
    setPickerFor(null);
    setPickerQuery("");
  };

  return (
    <div
      className="fixed inset-0 z-[70] flex justify-end bg-black/50"
      role="dialog"
      aria-modal="true"
      aria-label="Налаштування розділу"
    >
      <div className="flex h-full w-full max-w-lg flex-col border-l bg-card shadow-2xl">
        <div className="flex items-center justify-between border-b px-5 py-3.5">
          <span className="text-sm font-bold">
            {creating ? "Новий розділ" : "Налаштування розділу"}
          </span>
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            aria-label="Закрити"
            className="rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4">
          {error && (
            <p role="alert" className="mb-3 rounded-lg bg-destructive-soft px-3 py-2 text-xs text-destructive-soft-foreground">
              {error}
            </p>
          )}

          <div className="mb-3">
            <label className="mb-1 block text-xs font-semibold">
              Назва розділу <span className="text-destructive">*</span>
            </label>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full rounded-lg border bg-background px-3 py-2 text-sm outline-none focus:ring-1 focus:ring-ring"
            />
          </div>
          <div className="mb-3">
            <label className="mb-1 block text-xs font-semibold">Опис</label>
            <input
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="w-full rounded-lg border bg-background px-3 py-2 text-sm outline-none focus:ring-1 focus:ring-ring"
            />
          </div>

          <div className="mb-3">
            <label className="mb-1 block text-xs font-semibold">
              Власник <span className="text-destructive">*</span>{" "}
              <span className="font-normal text-muted-foreground">
                (публікує, папки/pin/архів; зміна → confirm + аудит)
              </span>
            </label>
            <PrincipalChip value={owner} onPick={isKbAdmin ? () => setPickerFor("owner") : undefined} />
          </div>
          <div className="mb-3">
            <label className="mb-1 block text-xs font-semibold">
              Резервний власник{" "}
              <span className="font-normal text-muted-foreground">(повні права власника)</span>
            </label>
            <PrincipalChip
              value={backup}
              onPick={isKbAdmin ? () => setPickerFor("backup") : undefined}
              onClear={() => setBackup(null)}
            />
          </div>

          <div className="mb-3">
            <label className="mb-1 block text-xs font-semibold">
              Редактори{" "}
              <span className="font-normal text-muted-foreground">
                (чернетки; НЕ публікують; доступ мають завжди, незалежно від видимості)
              </span>
            </label>
            <div className="flex flex-wrap items-center gap-1.5 rounded-lg border px-2.5 py-2">
              {editors.map((e) => (
                <span
                  key={e.id}
                  className="inline-flex items-center gap-1 rounded-full border bg-muted px-2 py-0.5 text-xs font-medium"
                >
                  {e.name}
                  <button
                    type="button"
                    aria-label={`Прибрати ${e.name}`}
                    onClick={() => setEditors((prev) => prev.filter((x) => x.id !== e.id))}
                    className="text-muted-foreground hover:text-destructive"
                  >
                    ✕
                  </button>
                </span>
              ))}
              {isKbAdmin && (
                <button
                  type="button"
                  onClick={() => setPickerFor("editor")}
                  className="text-xs text-muted-foreground hover:text-foreground"
                >
                  + додати
                </button>
              )}
            </div>
          </div>

          {pickerFor !== null && (
            <div className="mb-3 rounded-lg border border-dashed p-3">
              <div className="mb-1 text-xs font-semibold">
                {pickerFor === "owner" ? "Новий власник" : pickerFor === "backup" ? "Резервний власник" : "Додати редактора"}
              </div>
              <input
                autoFocus
                value={pickerQuery}
                onChange={(e) => setPickerQuery(e.target.value)}
                placeholder="Пошук (active internal, без portal/QA)…"
                className="w-full rounded-lg border bg-background px-3 py-1.5 text-sm outline-none focus:ring-1 focus:ring-ring"
              />
              {(pickerSearch.data?.users ?? []).length > 0 && (
                <ul className="mt-1 max-h-36 overflow-y-auto rounded-lg border bg-card text-sm">
                  {pickerSearch.data!.users.map((u) => (
                    <li key={u.id}>
                      <button
                        type="button"
                        className="w-full px-3 py-1.5 text-left hover:bg-muted"
                        onClick={() => pickPrincipal(u)}
                      >
                        {u.name}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              <button
                type="button"
                onClick={() => setPickerFor(null)}
                className="mt-1.5 text-xs text-muted-foreground hover:text-foreground"
              >
                Скасувати
              </button>
            </div>
          )}

          <div className="mb-2">
            <label className="mb-1 block text-xs font-semibold">Видимість (хто читає)</label>
            <div className="inline-flex overflow-hidden rounded-full border" role="tablist" aria-label="Видимість">
              {(
                [
                  ["all", "Усі співробітники"],
                  ["teams", "Обрані команди"],
                ] as const
              ).map(([m, label]) => (
                <button
                  key={m}
                  type="button"
                  role="tab"
                  aria-selected={mode === m}
                  onClick={() => setMode(m)}
                  className={cn(
                    "px-3.5 py-1.5 text-xs font-semibold",
                    mode === m ? "bg-primary/10 text-primary" : "text-muted-foreground hover:text-foreground",
                  )}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          {mode === "teams" && (
            <div className="mb-3">
              <div className="flex flex-wrap items-center gap-1.5 rounded-lg border px-2.5 py-2">
                {teams.map((t) =>
                  t.missing ? (
                    <span
                      key={t.id}
                      className="inline-flex items-center gap-1 rounded-full bg-destructive-soft px-2 py-0.5 text-xs font-semibold text-destructive-soft-foreground"
                    >
                      команда недоступна
                      <button
                        type="button"
                        aria-label="Прибрати недоступну команду"
                        onClick={() => setTeams((prev) => prev.filter((x) => x.id !== t.id))}
                      >
                        ✕
                      </button>
                    </span>
                  ) : (
                    <span
                      key={t.id}
                      className="inline-flex items-center gap-1 rounded-full bg-info px-2 py-0.5 text-xs font-semibold text-info-foreground"
                    >
                      {t.name}
                      <button
                        type="button"
                        aria-label={`Прибрати ${t.name}`}
                        onClick={() => setTeams((prev) => prev.filter((x) => x.id !== t.id))}
                      >
                        ✕
                      </button>
                    </span>
                  ),
                )}
                {isKbAdmin && (
                  <select
                    value=""
                    onChange={(e) => {
                      const t = (teamsQ.data?.teams ?? []).find((x) => x.id === e.target.value);
                      if (t && !teams.some((x) => x.id === t.id))
                        setTeams((prev) => [...prev, { id: t.id, name: t.name }]);
                    }}
                    className="rounded border bg-background px-2 py-1 text-xs text-muted-foreground outline-none"
                  >
                    <option value="">+ Додати команду</option>
                    {(teamsQ.data?.teams ?? [])
                      .filter((t) => !teams.some((x) => x.id === t.id))
                      .map((t) => (
                        <option key={t.id} value={t.id}>
                          {t.name} ({t.memberCount})
                        </option>
                      ))}
                  </select>
                )}
              </div>

              {aliveTeamIds.length === 0 ? (
                <div className="mt-2 flex items-start gap-2 rounded-lg bg-warning px-3 py-2 text-xs text-warning-foreground">
                  <TriangleAlert className="mt-0.5 h-3.5 w-3.5 flex-none" aria-hidden />
                  Жодної команди не обрано. Розділ бачитимуть лише власник, резервний власник,
                  редактори та адміністратори бази знань.
                </div>
              ) : (
                isKbAdmin && (
                  <div className="mt-2 rounded-lg border px-3 py-2 text-xs text-muted-foreground">
                    Бачитимуть: <b className="text-foreground">{audienceQ.data?.count ?? "…"} співробітники</b> +
                    власник, резервний власник та {editors.length} редактори
                  </div>
                )
              )}
            </div>
          )}
        </div>

        <div className="flex items-center justify-between border-t px-5 py-3.5">
          {!creating ? (
            section!.isArchived ? (
              <button
                type="button"
                disabled={busy}
                onClick={() => {
                  setBusy(true);
                  restoreKbSection(section!.id).then(finish).catch(fail);
                }}
                className="rounded-md border px-3.5 py-2 text-sm font-medium hover:bg-accent disabled:opacity-50"
              >
                Відновити розділ
              </button>
            ) : (
              <button
                type="button"
                disabled={busy}
                onClick={() =>
                  setConfirm({
                    text: "Розділ і всі його матеріали стануть прихованими від читачів (списки, пошук, AI). Статуси матеріалів НЕ зміняться; відновлення поверне все як було.",
                    run: () => {
                      setConfirm(null);
                      setBusy(true);
                      archiveKbSection(section!.id).then(finish).catch(fail);
                    },
                  })
                }
                className="rounded-md px-3.5 py-2 text-sm font-medium text-destructive hover:bg-destructive-soft disabled:opacity-50"
              >
                Архівувати розділ
              </button>
            )
          ) : (
            <span />
          )}
          <button
            type="button"
            disabled={busy}
            onClick={save}
            className="rounded-md bg-primary px-3.5 py-2 text-sm font-semibold text-primary-foreground disabled:opacity-50"
          >
            {busy ? "Зберігаємо…" : "Зберегти"}
          </button>
        </div>
      </div>

      {confirm && (
        <div
          className="fixed inset-0 z-[80] flex items-center justify-center bg-black/50 p-4"
          role="dialog"
          aria-modal="true"
          aria-label="Підтвердження"
        >
          <div className="w-full max-w-md rounded-xl border bg-card p-5 shadow-2xl">
            <p className="text-sm">{confirm.text}</p>
            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setConfirm(null)}
                className="rounded-md px-3.5 py-2 text-sm font-medium text-muted-foreground hover:bg-muted"
              >
                Скасувати
              </button>
              <button
                type="button"
                onClick={confirm.run}
                className="rounded-md bg-primary px-3.5 py-2 text-sm font-semibold text-primary-foreground"
              >
                Підтвердити
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function PrincipalChip({
  value,
  onPick,
  onClear,
}: {
  value: KbPrincipal | null;
  onPick?: () => void;
  onClear?: () => void;
}) {
  return (
    <div className="flex items-center gap-2 rounded-lg border px-3 py-2 text-sm">
      <span className="min-w-0 flex-1 truncate">{value?.name ?? "— не призначено —"}</span>
      {value && onClear && (
        <button
          type="button"
          onClick={onClear}
          aria-label="Прибрати"
          className="text-xs text-muted-foreground hover:text-destructive"
        >
          ✕
        </button>
      )}
      {onPick && (
        <button
          type="button"
          onClick={onPick}
          className="text-xs font-semibold text-primary hover:underline"
        >
          Змінити
        </button>
      )}
    </div>
  );
}
